import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useColorModeValue } from "./ui/color-mode";
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  ButtonGroup,
  Card,
  Container,
  Flex,
  Heading,
  HStack,
  Icon,
  Input,
  NativeSelect,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
  Badge,
  Field,
} from '@chakra-ui/react';
import { Avatar } from '@/components/ui/avatar';
import { toaster } from '@/components/ui/toaster';
import { InputGroup, InputLeftElement } from '@/components/ui/input-group';
import { motion, AnimatePresence } from 'framer-motion';
import { FaDownload, FaExternalLinkAlt, FaFileAlt, FaList, FaLock, FaSearch, FaTh } from 'react-icons/fa';
import Footer from './Footer.jsx';
import SEO from './SEO.jsx';
import { documentosBandaPublicAPI } from '../services/api';

const MotionBox = motion.create(Box);

const CATEGORIAS = [
  { value: 'estatutos', label: 'Estatutos' },
  { value: 'normativa_interna', label: 'Normativa interna' },
  { value: 'actas', label: 'Actas' },
  { value: 'permisos_parentales', label: 'Permisos parentales' },
  { value: 'cruceta_planificacion', label: 'Cruceta / planificación anual' },
  { value: 'semana_santa', label: 'Normas de Semana Santa' },
  { value: 'otros', label: 'Otros' },
];

const labelCategoria = (c) => CATEGORIAS.find((x) => x.value === c)?.label || c;

const ORDEN_OPTIONS = [
  { value: 'predeterminado', label: 'Orden predeterminado' },
  { value: 'reciente', label: 'Más recientes primero' },
  { value: 'antiguo', label: 'Más antiguos primero' },
  { value: 'titulo_az', label: 'Título (A → Z)' },
  { value: 'titulo_za', label: 'Título (Z → A)' },
  { value: 'categoria', label: 'Por categoría' },
  { value: 'tamano_desc', label: 'Tamaño (mayor primero)' },
  { value: 'tamano_asc', label: 'Tamaño (menor primero)' },
];

const VISTA_TABLON_KEY = 'cadencya.tablon.vista';

export default function DocumentosBandaPublic() {
  const { token } = useParams();
  
  const bg = useColorModeValue('linear-gradient(160deg, #f5f3ff 0%, #ecfeff 45%, #fff 100%)', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderCol = useColorModeValue('purple.100', 'whiteAlpha.200');
  const subtle = useColorModeValue('gray.600', 'gray.400');
  const docRowBorder = useColorModeValue('gray.100', 'whiteAlpha.200');
  const docRowHoverBorder = useColorModeValue('purple.200', 'purple.400');
  const docRowHoverBg = useColorModeValue('purple.50', 'whiteAlpha.50');

  const [loadingMeta, setLoadingMeta] = useState(true);
  const [meta, setMeta] = useState(null);
  const [gateError, setGateError] = useState(null);
  const [pwd, setPwd] = useState('');
  const [unlocking, setUnlocking] = useState(false);
  const [accessToken, setAccessToken] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [dlId, setDlId] = useState(null);
  const [openingId, setOpeningId] = useState(null);

  const [busqueda, setBusqueda] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [orden, setOrden] = useState('predeterminado');
  const [vista, setVista] = useState(() => {
    try {
      const v = localStorage.getItem(VISTA_TABLON_KEY);
      return v === 'lista' ? 'lista' : 'mosaico';
    } catch {
      return 'mosaico';
    }
  });

  const persistVista = useCallback((next) => {
    const v = next === 'lista' ? 'lista' : 'mosaico';
    setVista(v);
    try {
      localStorage.setItem(VISTA_TABLON_KEY, v);
    } catch {
      /* ignore */
    }
  }, []);

  const documentosFiltrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    let list = docs.filter((d) => {
      if (filtroCategoria && d.categoria !== filtroCategoria) return false;
      if (!q) return true;
      return [d.titulo, d.descripcion, d.nombreArchivo].some(
        (s) => s != null && String(s).toLowerCase().includes(q),
      );
    });
    const sorted = [...list];
    const tituloCmp = (a, b) =>
      String(a.titulo || '').localeCompare(String(b.titulo || ''), 'es', { sensitivity: 'base' });
    switch (orden) {
      case 'reciente':
        sorted.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;
      case 'antiguo':
        sorted.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        break;
      case 'titulo_az':
        sorted.sort(tituloCmp);
        break;
      case 'titulo_za':
        sorted.sort((a, b) => tituloCmp(b, a));
        break;
      case 'categoria':
        sorted.sort((a, b) => {
          const c = String(a.categoria || '').localeCompare(String(b.categoria || ''));
          return c !== 0 ? c : tituloCmp(a, b);
        });
        break;
      case 'tamano_desc':
        sorted.sort((a, b) => (b.tamanoBytes ?? 0) - (a.tamanoBytes ?? 0));
        break;
      case 'tamano_asc':
        sorted.sort((a, b) => (a.tamanoBytes ?? 0) - (b.tamanoBytes ?? 0));
        break;
      default:
        break;
    }
    return sorted;
  }, [docs, busqueda, filtroCategoria, orden]);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!token) return;
      setLoadingMeta(true);
      setGateError(null);
      try {
        const { data } = await documentosBandaPublicAPI.meta(token);
        if (!alive) return;
        setMeta(data);
        if (!data?.requiresPassword) {
          const u = await documentosBandaPublicAPI.unlock(token, {});
          if (!alive) return;
          setAccessToken(u.data?.accessToken || null);
        }
      } catch (e) {
        if (alive) setGateError(e?.response?.data?.message || 'Enlace no válido');
      } finally {
        if (alive) setLoadingMeta(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [token]);

  useEffect(() => {
    if (!token || !accessToken) return;
    let alive = true;
    (async () => {
      setLoadingDocs(true);
      try {
        const { data } = await documentosBandaPublicAPI.listDocumentos(token, accessToken);
        if (!alive) return;
        setDocs(Array.isArray(data?.documentos) ? data.documentos : []);
      } catch {
        if (alive) setDocs([]);
      } finally {
        if (alive) setLoadingDocs(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [token, accessToken]);

  const submitPwd = useCallback(async () => {
    if (!token) return;
    setUnlocking(true);
    setGateError(null);
    try {
      const { data } = await documentosBandaPublicAPI.unlock(token, { password: pwd });
      setAccessToken(data?.accessToken || null);
    } catch (e) {
      setGateError(e?.response?.data?.message || 'No se pudo acceder');
    } finally {
      setUnlocking(false);
    }
  }, [token, pwd]);

  const abrirDocumento = async (d) => {
    if (!token || !accessToken) return;
    setOpeningId(d.id);
    try {
      const blob = await documentosBandaPublicAPI.fetchDocumentoArchivoBlob(token, accessToken, d.id);
      const mime = d.mimeType || blob.type || 'application/octet-stream';
      const blobConTipo = blob.type ? blob : new Blob([blob], { type: mime });
      const url = URL.createObjectURL(blobConTipo);
      const win = window.open(url, '_blank', 'noopener,noreferrer');
      if (!win) {
        URL.revokeObjectURL(url);
        toaster.create({
          title: 'Ventana bloqueada',
          description: 'Permite ventanas emergentes para ver el documento.',
          type: 'warning',
        });
        return;
      }
      window.setTimeout(() => URL.revokeObjectURL(url), 180000);
    } catch {
      toaster.create({ title: 'No se pudo abrir el documento', type: 'error' });
    } finally {
      setOpeningId(null);
    }
  };

  const download = async (doc) => {
    if (!token || !accessToken) return;
    setDlId(doc.id);
    try {
      const blob = await documentosBandaPublicAPI.fetchDocumentoArchivoBlob(token, accessToken, doc.id);
      const safe =
        String(doc.nombreArchivo || doc.titulo || 'documento')
          .replace(/[/\\?%*:|"<>]/g, '')
          .replace(/\s+/g, '_')
          .slice(0, 120) || 'documento';
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = safe;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toaster.create({ title: 'Descarga iniciada', type: 'success' });
    } catch {
      toaster.create({ title: 'No se pudo descargar', type: 'error' });
    } finally {
      setDlId(null);
    }
  };

  if (!token) {
    return (
      <Box minH="100vh" bg={bg} py={10}>
        <Container maxW="md">
          <Text>Enlace no válido.</Text>
        </Container>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg={bg}>
      <SEO
        title="Tablón de documentos | Cadencya"
        description="Tablón seguro: documentos que la agrupación comparte mediante enlace y contraseña."
      />
      <Container maxW="6xl" py={{ base: 8, md: 12 }} px={4}>
        <MotionBox
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        >
          <VStack gap={8} align="stretch">
            <HStack gap={4} align="start">
              <Avatar.Root size="lg" bg="purple.500"><Avatar.Fallback name={meta?.bandaNombre || 'Banda'} /><Avatar.Image src={meta?.bandaAvatar || undefined} /></Avatar.Root>
              <Box flex={1}>
                <Heading size="lg" color="purple.700" letterSpacing="-0.02em">
                  Tablón
                </Heading>
                {meta?.bandaNombre && (
                  <Text fontWeight="600" color="purple.600" fontSize="md" mt={0.5}>
                    {meta.bandaNombre}
                  </Text>
                )}
                <Text fontSize="sm" color="gray.600" mt={2}>
                  Aquí ves solo lo que la banda ha publicado en su tablón. El acceso puede estar protegido con contraseña.
                </Text>
              </Box>
            </HStack>

            {loadingMeta && (
              <HStack justify="center" py={10}>
                <Spinner color="purple.500" size="lg" />
              </HStack>
            )}

            {!loadingMeta && gateError && !meta && (
              <Card.Root bg={cardBg} borderWidth="1px" borderColor="red.200">
                <Card.Body>
                  <Text color="red.600">{gateError}</Text>
                </Card.Body>
              </Card.Root>
            )}

            {!loadingMeta && meta?.requiresPassword && !accessToken && (
              <Card.Root bg={cardBg} shadow="xl" borderRadius="2xl" borderWidth="1px" borderColor={borderCol} overflow="hidden">
                <Box h="4px" bgGradient="linear(to-r, purple.500, cyan.400)" />
                <Card.Body>
                  <VStack align="stretch" gap={4}>
                    <HStack>
                      <Icon color="purple.500" boxSize={6} asChild><FaLock /></Icon>
                      <Heading size="md">Contraseña del tablón</Heading>
                    </HStack>
                    <Field.Root>
                      <Field.Label fontSize="sm">Introduce la clave que te ha facilitado la banda</Field.Label>
                      <Input
                        type="password"
                        value={pwd}
                        onChange={(e) => setPwd(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && submitPwd()}
                        borderRadius="lg"
                      />
                    </Field.Root>
                    {gateError && (
                      <Text fontSize="sm" color="red.500">
                        {gateError}
                      </Text>
                    )}
                    <Button colorPalette="purple" size="lg" onClick={submitPwd} loading={unlocking}>
                      Acceder
                    </Button>
                  </VStack>
                </Card.Body>
              </Card.Root>
            )}

            {!loadingMeta && accessToken && (
              <VStack align="stretch" gap={4}>
                {loadingDocs ? (
                  <HStack justify="center" py={8}>
                    <Spinner color="purple.500" />
                  </HStack>
                ) : docs.length === 0 ? (
                  <Card.Root bg={cardBg} borderWidth="1px" borderColor={borderCol} shadow="md" rounded="2xl" overflow="hidden">
                    <Box h="3px" bgGradient="linear(to-r, purple.500, cyan.400)" />
                    <Card.Body>
                      <Text color="gray.600">No hay documentos en el tablón por ahora.</Text>
                    </Card.Body>
                  </Card.Root>
                ) : (
                  <Card.Root bg={cardBg} shadow="lg" rounded="2xl" overflow="hidden">
                    <Box h="3px" bgGradient="linear(to-r, purple.500, cyan.400)" />
                    <Card.Header pb={3}>
                      <Flex
                        direction={{ base: 'column', md: 'row' }}
                        gap={4}
                        align={{ md: 'flex-start' }}
                        justify="space-between"
                      >
                        <Box flex={1} minW={0}>
                          <Heading size="md">Documentos del tablón</Heading>
                          <Text fontSize="sm" color={subtle} mt={1}>
                            {`${docs.length} documento${docs.length === 1 ? '' : 's'}${
                              documentosFiltrados.length !== docs.length
                                ? ` · ${documentosFiltrados.length} con los filtros actuales`
                                : ''
                            }`}
                          </Text>
                        </Box>
                      </Flex>
                      <VStack gap={3} align="stretch" mt={5}>
                        <InputGroup>
                          <InputLeftElement pointerEvents="none">
                            <Icon color="gray.400" asChild><FaSearch /></Icon>
                          </InputLeftElement>
                          <Input
                            pl={10}
                            placeholder="Buscar por título, descripción o nombre de archivo…"
                            value={busqueda}
                            onChange={(e) => setBusqueda(e.target.value)}
                            borderRadius="xl"
                          />
                        </InputGroup>
                        <Flex direction={{ base: 'column', lg: 'row' }} gap={3} align={{ lg: 'flex-end' }} flexWrap="wrap">
                          <Field.Root flex={{ lg: '1 1 160px' }} minW={0}>
                            <Field.Label fontSize="xs" mb={1} color={subtle}>
                              Categoría
                            </Field.Label>
                            <NativeSelect.Root>
                              <NativeSelect.Field
                                size="sm"
                                borderRadius="lg"
                                value={filtroCategoria}
                                onChange={(e) => setFiltroCategoria(e.target.value)}>
                                <option value="">Todas las categorías</option>
                                {CATEGORIAS.map((c) => (
                                  <option key={c.value} value={c.value}>
                                    {c.label}
                                  </option>
                                ))}
                              </NativeSelect.Field>
                              <NativeSelect.Indicator />
                            </NativeSelect.Root>
                          </Field.Root>
                          <Field.Root flex={{ lg: '1 1 200px' }} minW={0}>
                            <Field.Label fontSize="xs" mb={1} color={subtle}>
                              Ordenar por
                            </Field.Label>
                            <NativeSelect.Root>
                              <NativeSelect.Field
                                size="sm"
                                borderRadius="lg"
                                value={orden}
                                onChange={(e) => setOrden(e.target.value)}>
                                {ORDEN_OPTIONS.map((o) => (
                                  <option key={o.value} value={o.value}>
                                    {o.label}
                                  </option>
                                ))}
                              </NativeSelect.Field>
                              <NativeSelect.Indicator />
                            </NativeSelect.Root>
                          </Field.Root>
                          <Field.Root flexShrink={0} w={{ base: 'full', lg: 'auto' }}>
                            <Field.Label fontSize="xs" mb={1} color={subtle}>
                              Visualización
                            </Field.Label>
                            <ButtonGroup attached size="sm" w={{ base: 'full', lg: 'auto' }}>
                              <Button
                                variant={vista === 'lista' ? 'solid' : 'outline'}
                                colorPalette="purple"
                                flex={{ base: 1, lg: 'initial' }}
                                onClick={() => persistVista('lista')}
                                aria-pressed={vista === 'lista'}><FaList />Lista
                                                              </Button>
                              <Button
                                variant={vista === 'mosaico' ? 'solid' : 'outline'}
                                colorPalette="purple"
                                flex={{ base: 1, lg: 'initial' }}
                                onClick={() => persistVista('mosaico')}
                                aria-pressed={vista === 'mosaico'}><FaTh />Mosaico
                                                              </Button>
                            </ButtonGroup>
                          </Field.Root>
                        </Flex>
                      </VStack>
                    </Card.Header>
                    <Card.Body pt={0}>
                      {documentosFiltrados.length === 0 ? (
                        <Text color={subtle} py={6}>
                          Ningún documento coincide con la búsqueda o el filtro. Prueba a limpiar criterios.
                        </Text>
                      ) : vista === 'lista' ? (
                        <AnimatePresence>
                          <VStack align="stretch" gap={3}>
                            {documentosFiltrados.map((d, i) => (
                              <MotionBox
                                key={d.id}
                                initial={{ opacity: 0, y: 6 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.2, delay: Math.min(i * 0.02, 0.24) }}
                              >
                                <HStack
                                  p={4}
                                  borderWidth="1px"
                                  borderRadius="xl"
                                  borderColor={docRowBorder}
                                  justify="space-between"
                                  align="start"
                                  _hover={{ borderColor: docRowHoverBorder, bg: docRowHoverBg }}
                                  transition="border-color 0.2s, background 0.2s"
                                >
                                  <Box minW={0} flex={1}>
                                    <Text fontWeight="700" lineClamp={2}>
                                      {d.titulo}
                                    </Text>
                                    <HStack mt={1} gap={2} flexWrap="wrap">
                                      <Badge colorPalette="purple">{labelCategoria(d.categoria)}</Badge>
                                      {d.nombreArchivo && (
                                        <Text fontSize="xs" color={subtle} lineClamp={1} maxW="200px" title={d.nombreArchivo}>
                                          {d.nombreArchivo}
                                        </Text>
                                      )}
                                      {d.tamanoBytes != null && (
                                        <Text fontSize="xs" color={subtle}>
                                          {(d.tamanoBytes / 1024).toFixed(0)} KB
                                        </Text>
                                      )}
                                      {d.createdAt && (
                                        <Text fontSize="xs" color={subtle}>
                                          {new Date(d.createdAt).toLocaleDateString('es-ES', {
                                            day: 'numeric',
                                            month: 'short',
                                            year: 'numeric',
                                          })}
                                        </Text>
                                      )}
                                    </HStack>
                                    {d.descripcion && (
                                      <Text fontSize="sm" color={subtle} mt={2} lineClamp={3}>
                                        {d.descripcion}
                                      </Text>
                                    )}
                                  </Box>
                                  <HStack gap={1} flexShrink={0} flexWrap="wrap" justify="flex-end">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      colorPalette="purple"
                                      loading={openingId === d.id}
                                      loadingText="Abriendo"
                                      onClick={() => abrirDocumento(d)}><FaExternalLinkAlt />Abrir
                                                                          </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      colorPalette="purple"
                                      onClick={() => download(d)}
                                      loading={dlId === d.id}><FaDownload />Descargar
                                                                          </Button>
                                  </HStack>
                                </HStack>
                              </MotionBox>
                            ))}
                          </VStack>
                        </AnimatePresence>
                      ) : (
                        <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} gap={4}>
                          {documentosFiltrados.map((d, i) => (
                            <MotionBox
                              key={d.id}
                              initial={{ opacity: 0, scale: 0.97 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ duration: 0.2, delay: Math.min(i * 0.03, 0.3) }}
                            >
                              <Box
                                p={4}
                                borderWidth="1px"
                                borderRadius="xl"
                                borderColor={docRowBorder}
                                bg={cardBg}
                                h="full"
                                minH="240px"
                                display="flex"
                                flexDirection="column"
                                _hover={{ borderColor: docRowHoverBorder, bg: docRowHoverBg }}
                                transition="border-color 0.2s, background 0.2s"
                                boxShadow="sm"
                              >
                                <HStack justify="space-between" align="start" mb={2}>
                                  <Flex
                                    align="center"
                                    justify="center"
                                    w={10}
                                    h={10}
                                    borderRadius="lg"
                                    bg="purple.50"
                                    color="purple.600"
                                    _dark={{ bg: 'whiteAlpha.100', color: 'purple.200' }}
                                  >
                                    <Icon boxSize={5} asChild><FaFileAlt /></Icon>
                                  </Flex>
                                </HStack>
                                <Text fontWeight="700" fontSize="md" lineClamp={2} lineHeight="short">
                                  {d.titulo}
                                </Text>
                                <Badge colorPalette="purple" alignSelf="start" mt={2}>
                                  {labelCategoria(d.categoria)}
                                </Badge>
                                <VStack align="stretch" gap={0} mt={2} flex={1}>
                                  {d.nombreArchivo && (
                                    <Text fontSize="xs" color={subtle} lineClamp={1} title={d.nombreArchivo}>
                                      {d.nombreArchivo}
                                    </Text>
                                  )}
                                  <HStack gap={2} flexWrap="wrap" fontSize="xs" color={subtle}>
                                    {d.tamanoBytes != null && <Text>{(d.tamanoBytes / 1024).toFixed(0)} KB</Text>}
                                    {d.createdAt && (
                                      <Text>
                                        {new Date(d.createdAt).toLocaleDateString('es-ES', {
                                          day: 'numeric',
                                          month: 'short',
                                          year: 'numeric',
                                        })}
                                      </Text>
                                    )}
                                  </HStack>
                                  {d.descripcion && (
                                    <Text fontSize="sm" color={subtle} mt={2} lineClamp={3} flex={1}>
                                      {d.descripcion}
                                    </Text>
                                  )}
                                </VStack>
                                <VStack gap={2} w="full" mt={4}>
                                  <Button
                                    size="sm"
                                    colorPalette="purple"
                                    loading={openingId === d.id}
                                    loadingText="Abriendo"
                                    onClick={() => abrirDocumento(d)}
                                    w="full"><FaExternalLinkAlt />Abrir
                                                                      </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    colorPalette="purple"
                                    onClick={() => download(d)}
                                    loading={dlId === d.id}
                                    w="full"><FaDownload />Descargar
                                                                      </Button>
                                </VStack>
                              </Box>
                            </MotionBox>
                          ))}
                        </SimpleGrid>
                      )}
                    </Card.Body>
                  </Card.Root>
                )}
              </VStack>
            )}
          </VStack>
        </MotionBox>
      </Container>
      <Footer />
    </Box>
  );
}
